/**
 * Permission helpers for Platform /v1/me privileges.
 * Prefer platform_permissions when present; fall back to legacy SUPERADMIN|ADMIN|VIEWER.
 */

/** Path → required permission (any one). /me and account pages are always allowed. */
export const ROUTE_PERMISSIONS = {
  '/dashboard': 'sites:read',
  '/devices': 'devices:read',
  '/alarms': 'alarms:read',
  '/monitoring': 'energy:read',
  '/reports': 'reports:request',
  '/digital-twin': 'sites:read',
  '/production-planning': 'energy:read',
  '/control': 'commands:request',
  '/companies': 'companies:read',
  '/users': 'users:read',
  '/analysis': 'energy:read',
  '/dashboard/pv-fleet': 'energy:read',
  '/connections': 'gateways:read',
  '/platform-lab': 'users:write',
};

/** Legacy role fallback when platform_permissions is missing (old session). */
const LEGACY_ROLE_PERMISSIONS = {
  SUPERADMIN: '*',
  ADMIN: [
    'sites:read', 'sites:write', 'gateways:read', 'gateways:write',
    'devices:read', 'devices:write', 'tags:read', 'tags:write',
    'alarms:read', 'alarms:acknowledge', 'alarms:write', 'commands:request', 'commands:approve',
    'config:read', 'config:draft', 'config:publish', 'config:report',
    'energy:read', 'energy:write', 'reports:request', 'history:read', 'audit:read',
    'users:read', 'users:write', 'companies:read', 'companies:write',
  ],
  VIEWER: [
    'sites:read', 'gateways:read', 'devices:read', 'tags:read', 'alarms:read',
    'config:read', 'energy:read', 'history:read', 'audit:read', 'users:read', 'companies:read',
  ],
};

function permissionSet(user) {
  if (!user) return null;
  if (user.platform_admin || user.role === 'SUPERADMIN') {
    return '*';
  }
  const perms = user.platform_permissions;
  if (Array.isArray(perms) && perms.length > 0) {
    return new Set(perms);
  }
  const legacy = LEGACY_ROLE_PERMISSIONS[user.role];
  if (legacy === '*') return '*';
  if (Array.isArray(legacy)) return new Set(legacy);
  return new Set();
}

/** True if the session has the given permission code. */
export function userHasPermission(user, code) {
  if (!user || !code) return false;
  const set = permissionSet(user);
  if (set === '*') return true;
  if (!set) return false;
  return set.has(code);
}

/** True if user has every listed permission. */
export function userHasAllPermissions(user, codes = []) {
  return codes.every((c) => userHasPermission(user, c));
}

/** Stored user route lists may still contain the pre-rename path. */
export function normalizeAllowedRoutes(routes) {
  if (!Array.isArray(routes)) return routes;
  return [...new Set(routes.map((p) => (p === '/kosova' ? '/monitoring' : p)))];
}

/**
 * Route access: explicit allowed_routes (if set) AND required permission for the path.
 * /me, /settings, and /about are always open to authenticated users.
 */
export function userCanAccessPath(user, pathname) {
  if (!user || !pathname) return false;
  const base = pathname.split('?')[0];
  if (base === '/me' || base === '/settings' || base === '/about') {
    return true;
  }

  const allowedPaths =
    user.allowed_routes && Array.isArray(user.allowed_routes) && user.allowed_routes.length > 0
      ? user.allowed_routes
      : null;
  if (allowedPaths !== null) {
    const ok = allowedPaths.some((p) => base === p || base.startsWith(`${p}/`));
    if (!ok) return false;
  }

  // Find longest matching route key
  let required = null;
  let bestLen = -1;
  for (const [path, perm] of Object.entries(ROUTE_PERMISSIONS)) {
    if (base === path || base.startsWith(`${path}/`)) {
      if (path.length > bestLen) {
        bestLen = path.length;
        required = perm;
      }
    }
  }
  if (!required) return true;
  return userHasPermission(user, required);
}

/** Menu item visibility: permission (if set) then legacy roles, then allowed_routes. */
export function userCanSeeMenuItem(user, item) {
  if (!user || !item) return false;
  if (item.path === '/me') return true;

  if (Array.isArray(item.activePaths) && item.activePaths.length > 0) {
    return item.activePaths.some((path) => userCanAccessPath(user, path));
  }

  if (item.permission) {
    if (!userHasPermission(user, item.permission)) return false;
  } else if (item.roles && item.roles.length > 0) {
    if (!item.roles.includes(user.role)) return false;
  }

  const allowedPaths =
    user.allowed_routes && Array.isArray(user.allowed_routes) && user.allowed_routes.length > 0
      ? user.allowed_routes
      : null;
  if (allowedPaths !== null && !allowedPaths.includes(item.path)) return false;
  return true;
}
